import * as React from "react";
import { connect } from "react-redux";
import { Route, Routes } from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";
import { LoginUserActions } from "redux/actions/LoginUserActions";

import { LoginUser } from "models/LoginUser";
import { ModalData } from "models/ModalData";
import { StoreModel } from "models/StoreModel";
import { UiMessage, UiMessageType } from "models/UiMessageModel";

import Modal from "app/components/common/Modal";
import UiMessageViewer from "app/components/common/UiMessageViewer";
import { Footer } from "app/components/layout/Footer";
import { LeftBar } from "app/components/layout/Leftbar";
import TopBar from "app/components/layout/Topbar";

import Dashboard from "app/pages/Dashboard/Dashboard";
import AccountDetails from "app/pages/account/AccountDetails";
import Register from "app/pages/account/Register";
import RegisterSuccess from "app/pages/account/RegisterSuccess";
import DailyReport from "app/pages/machines/DailyReport";
import MachineHistories from "app/pages/machines/MachineHistories";
import MachineDefects from "app/pages/machines/MachineDefects";
import MachineStatistics from "app/pages/machines/MachineStatistics";
import UpcomingMaintenance from "app/pages/machines/UpcomingMaintenance";
import FuelTypes from "app/pages/settings/fuelTypes/Index";
import MachineParts from "app/pages/settings/machineParts/Index";
import MachineMaintenances from "app/pages/settings/machineMaintenances/Index";
import MachineTypes from "app/pages/settings/machineTypes/Index";
import Machines from "app/pages/settings/machines/Index";
import MachineMaintenanceDetails from "app/pages/settings/machineMaintenances/Details";
import Maintenances from "app/pages/settings/maintenance/Index";
import Manufacturers from "app/pages/settings/manufacturer/Index";
import Units from "app/pages/settings/units/Index";
import UsersDetails from "app/pages/settings/users/Details";
import Users from "app/pages/settings/users/Index";
import Workshop from "app/pages/workshop/Workshop";
import MyTasks from "app/pages/tasks/MyTasks";
import AllTasks from "app/pages/tasks/AllTasks";

import DeleteAccountSuccess from "app/pages/account/DeleteAccountSuccess";
import ResetPassword from "app/pages/account/ResetPassword";
import SetNewPassword from "app/pages/account/SetNewPassword";
import ConfirmDeleteAccount from "app/pages/account/ConfirmDeleteAccount";
import Nothing from "app/pages/misc/Nothing";
import { Landing } from "app/pages/misc/Landing";
import { Agb, Datenschutz, Impressum } from "app/pages/misc/Legal";
import { RouterState } from "redux-first-history";
import { UserRoleTypes } from "models/UserRole";
import { MobileNavMenuActions } from "redux/actions/MobileNavMenuActions";
import { SubscriptionActions } from "redux/actions/settings/SubscriptionActions";
import { CurrentSubscription } from "models/SubscriptionPlan";
import Subscriptions from "app/pages/settings/subscriptions/Index";
import TenantSettings from "app/pages/settings/tenant/Index";
import { TranslationsActions } from "redux/actions/TranslationActions";
import { applyTheme } from "infrastructure/theme";


interface Props {
    loginUser: LoginUser;
    modalData: ModalData;
    uiMessages: UiMessage[];
    fetchUserData: typeof LoginUserActions.fetchUserData;
    fetchSettings: typeof LoginUserActions.fetchSettings;
    loadTranslations: typeof TranslationsActions.loadTranslations;
    fetchCurrentSubscription: typeof SubscriptionActions.fetchCurrentSubscription;
    currentSubscription: CurrentSubscription | null;
    hitNavMenu: typeof MobileNavMenuActions.hitNavMenu;
    router: RouterState;
    navMenuToggle: boolean;
    mobileNavMenuToggle: boolean;
}

// public legal pages, reachable without login
const legalPages: Record<string, React.FC> = { "/IMPRESSUM": Impressum, "/DATENSCHUTZ": Datenschutz, "/AGB": Agb };

// Pages are connected components whose props come from the store, not from the router.
function page(Component: React.ComponentType<any>) {
    return <Component />;
}

interface LoggedInRouterProps {
    loginUser: LoginUser;
}

class App extends React.Component<Props> {
    componentDidMount() {
        if (this.loggedIn() && !this.props.loginUser.userData && !this.props.loginUser.userData!.email) {
            this.props.fetchUserData(this.props.loginUser.token);
        }
        if (this.loggedIn()) {
            this.props.fetchCurrentSubscription(this.props.loginUser.token);
            this.props.fetchSettings(this.props.loginUser.token);
        }
    }

    componentDidUpdate(prevProps: Props) {
        // a new login brings a new tenant, so its plan has to be loaded again
        if (this.loggedIn() && prevProps.loginUser?.token !== this.props.loginUser.token) {
            this.props.fetchCurrentSubscription(this.props.loginUser.token);
            this.props.fetchSettings(this.props.loginUser.token);
        }
        const prev = prevProps.loginUser?.settings;
        const settings = this.props.loginUser?.settings;
        if (settings && settings.styleMode !== prev?.styleMode) {
            applyTheme(settings.styleMode);
        }
        if (settings && settings.language !== prev?.language) {
            this.props.loadTranslations(settings.language);
        }
    }

    loggedIn() {
        return this.props.loginUser && !!this.props.loginUser.token;
    }

    pathname() {
        return (this.props.router.location?.pathname ?? "").toUpperCase();
    }

    showModal() {
        return (!this.loggedIn() || (this.props.modalData && this.props.modalData.isActive))
            && this.pathname() !== "/ACCOUNT/REGISTER"
            && this.pathname() !== "/DELETE/SUCCESS"
            && this.pathname() !== "/ACCOUNT/RESETPASSWORD"
            && !this.pathname().startsWith("/ACCOUNT/SETNEWPASSWORD/")
    }

    showMessages() {
        return (this.props.uiMessages && this.props.uiMessages.filter(um => um.messageType != UiMessageType.none).length > 0)
    }

    render() {
        // public landing page; the root stays the login so returning users are not slowed down
        if (this.pathname() === "/WELCOME") {
            return <Landing />;
        }
        const LegalPage = legalPages[this.pathname()];
        if (LegalPage) {
            return <LegalPage />;
        }
        return (
            <>
                {this.pathname() !== "/ACCOUNT/SETNEWPASSWORD" &&
                    this.pathname() !== "/ACCOUNT/RESETPASSWORD" &&
                    <TopBar />
                }
                <main className="c-page">
                    {this.showModal() &&
                        <Modal />
                    }
                    <LeftBar loginUser={this.props.loginUser} currentSubscription={this.props.currentSubscription} navMenuToggle={this.props.navMenuToggle} hitNavMenu={this.props.hitNavMenu} mobileNavMenuToggle={this.props.mobileNavMenuToggle} />
                    <Routes>
                        <Route path="/Account/register" element={page(Register)} />
                        <Route path="/Register/Success" element={page(RegisterSuccess)} />
                        <Route path="/Delete/Success" element={page(DeleteAccountSuccess)} />
                        <Route path="/Account/confirmDelete/:email/:token" element={page(ConfirmDeleteAccount)} />
                        <Route path="/Account/resetPassword" element={page(ResetPassword)} />
                        <Route path="/Account/setNewPassword/:email/:token" element={page(SetNewPassword)} />
                        <Route path="*" element={<LoggedInRouter loginUser={this.props.loginUser} />} />
                    </Routes>
                    {this.showMessages() &&
                        <UiMessageViewer />
                    }
                </main>
                <Footer />
            </>
        )
    }
};

const LoggedInRouter: React.FC<LoggedInRouterProps> = ({
    loginUser
}) => {
    if (loginUser && !!loginUser.token) {
        return (
            <Routes>
                <Route path="/" element={page(Dashboard)} />
                {/* Account */}
                <Route path="/Account/details" element={page(AccountDetails)} />
                {/* Machines */}
                <Route path="/Machines/dailyreport" element={page(DailyReport)} />
                <Route path="/Machines/dailyreport/:id" element={page(DailyReport)} />
                <Route path="/Machines/UpcomingMaintenance" element={page(UpcomingMaintenance)} />
                <Route path="/Machines/histories" element={page(MachineHistories)} />
                <Route path="/Machines/defects" element={page(MachineDefects)} />
                <Route path="/Machines/statistics" element={page(MachineStatistics)} />
                {/* Workshop */}
                <Route path="/Machines/Workshop" element={page(Workshop)} />
                <Route path="/Machines/Workshop/:id" element={page(Workshop)} />
                {/* Tasks */}
                <Route path="/Tasks" element={page(MyTasks)} />
                {/* Settings */}
                <Route path="*" element={<SettingsRouter loginUser={loginUser} />} />
            </Routes>
        );
    } else {
        return null;
    }
};

const SettingsRouter: React.FC<LoggedInRouterProps> = ({
    loginUser
}) => {
    const hasRole = (role: string) => !!loginUser.userData?.roles.find(r => r.name === role);
    if (hasRole(UserRoleTypes.poolManager) || hasRole(UserRoleTypes.admin)) {
        return (
            <Routes>
                {hasRole(UserRoleTypes.admin) && <Route path="/Subscriptions" element={page(Subscriptions)} />}
                {hasRole(UserRoleTypes.poolManager) && <Route path="/Tenant" element={page(TenantSettings)} />}
                {hasRole(UserRoleTypes.poolManager) && <Route path="/Tasks/all" element={page(AllTasks)} />}
                <Route path="/FuelTypes" element={page(FuelTypes)} />
                <Route path="/MachineParts" element={page(MachineParts)} />
                <Route path="/Machines" element={page(Machines)} />
                <Route path="/MachineMaintenances" element={page(MachineMaintenances)} />
                <Route path="/MachineMaintenances/add" element={page(MachineMaintenanceDetails)} />
                <Route path="/MachineMaintenances/edit/:id" element={page(MachineMaintenanceDetails)} />
                <Route path="/MachineTypes" element={page(MachineTypes)} />
                <Route path="/Maintenances" element={page(Maintenances)} />
                <Route path="/Manufacturers" element={page(Manufacturers)} />
                <Route path="/Units" element={page(Units)} />
                <Route path="/Users" element={page(Users)} />
                <Route path="/Users/add" element={page(UsersDetails)} />
                <Route path="/Users/edit/:id" element={page(UsersDetails)} />
                <Route path="*" element={page(Nothing)} />
            </Routes>
        );
    } else {
        return <Nothing />;
    }
};

function mapStateToProps(state: StoreModel) {
    return {
        loginUser: state.loginUser,
        currentSubscription: state.currentSubscription,
        modalData: state.modalData,
        uiMessages: state.uiMessages,
        router: state.router,
        navMenuToggle: state.navMenuToggle,
        mobileNavMenuToggle: state.mobileNavMenuToggle
    };
}
function mapDispatchToProps(dispatch: Dispatch) {
    return {
        fetchUserData: bindActionCreators(LoginUserActions.fetchUserData, dispatch),
        fetchSettings: bindActionCreators(LoginUserActions.fetchSettings, dispatch),
        loadTranslations: bindActionCreators(TranslationsActions.loadTranslations, dispatch),
        fetchCurrentSubscription: bindActionCreators(SubscriptionActions.fetchCurrentSubscription, dispatch),
        hitNavMenu: bindActionCreators(MobileNavMenuActions.hitNavMenu, dispatch)
    };
}
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(App);